import React, { useCallback, useState } from 'react';
import { DraggableObject } from '../../../../../lib/canvas';
import {
	BaseEntityNames,
	BUILT_IN_BASE_ENTITY_IDS,
	CanvasEntityDisabledReason,
	DefinitionEntityState,
	ExternalIntegrationNames,
	getFirstBaseEntityWithOwnBase,
	getIntegrationIdFromDefinitionEntity,
} from '@elyx-code/project-logic-tree';
import { EditorService } from '../../../../../services/editor';
import CanvasPopupBaseComponent from '../../../../../lib/popup/CanvasPopupBaseComponent';
import { useCanvasPopup } from '../../../../../services/editor/CanvasPopup';
import { getCanvasEntityDOMId } from '../../../../../services/editor/ui';
import './google-drive-file-picker-api-footer-action.css';
import FileSearchIcon from '../../../../../assets/file-search.svg';
import { Logger } from '@elyx-code/common-ts-utils';
import { popupNotification } from '../../../../../popup-notification';

// Declare Google's global variables for TypeScript
declare global {
	interface Window {
		gapi: any;
		google: any;
	}
}

export function getGoogleDriveFilePickerAPIFooterActionDOMId(
	entity: DefinitionEntityState,
): string {
	return (
		getCanvasEntityDOMId(entity) +
		'--google-drive-file-picker-api-footer-action'
	);
}

interface IGoogleDriveFilePickerAPIFooterActionProps {
	draggableObject: DraggableObject;
	entity: DefinitionEntityState;
	project: EditorService;
	parentDisabledReasons: CanvasEntityDisabledReason[];
}

export default function GoogleDriveFilePickerAPIFooterAction(
	props: IGoogleDriveFilePickerAPIFooterActionProps,
) {
	const id = getGoogleDriveFilePickerAPIFooterActionDOMId(props.entity);
	const project = props.project;
	const entity = props.entity;

	const [isPicking, setIsPicking] = useState(false);

	const resolveTooltipHelperText = (): string => {
		return 'Click to give Elyx access to some of your Google Drive files';
	};

	const disabled = !!props.parentDisabledReasons.length || isPicking;

	const memoizedCanvasPopupBody = useCallback(
		() => (
			<CanvasPopupBaseComponent
				containerClasses={['tooltip', 'tooltip--large-font']}
				id={id + '--tooltip--popup--body'}
			>
				{disabled ? (
					<div style={{ textAlign: 'center' }}>
						<p style={{ margin: 0 }}>
							{isPicking
								? 'Loading Google Picker...'
								: 'Picking files from Google Drive is currently not possible'}
						</p>
					</div>
				) : (
					<div style={{ textAlign: 'center' }}>
						<p style={{ margin: 0 }}>{resolveTooltipHelperText()}</p>
					</div>
				)}
			</CanvasPopupBaseComponent>
		),
		[disabled, id, isPicking],
	);

	const canvasPopupTooltip = useCanvasPopup(
		{
			editor: props.project,
			id: id + '--tooltip',
			anchorQuerySelector: '#' + id,
			arrowClasses: ['tooltip-arrow'],
			parentCanvasObject: props.draggableObject,
			placement: 'top',
			arrowContainerSelector: '#' + id + '--tooltip--popup--body',
		},
		memoizedCanvasPopupBody,
	).setupHover({
		closeDelay: 80,
		openDelay: 600,
	});

	const baseEntity = getFirstBaseEntityWithOwnBase(
		entity,
		BaseEntityNames.EXTERNAL_INTEGRATION_CONNECTION,
	);

	const builtInConnectionPropertyId =
		BUILT_IN_BASE_ENTITY_IDS['built-in-base-entity'][
			baseEntity.id as 'BUILT_IN_EXTERNAL_INTEGRATION_CONNECTION_ENTITY'
		].properties.connectionId.id;

	const integrationName: ExternalIntegrationNames | null =
		getIntegrationIdFromDefinitionEntity(entity);

	if (!integrationName) {
		Logger.warn(
			`The integration name for the entity ${props.entity.id} could not be resolved.`,
		);
		return null;
	}

	const connectionIdProperty = entity.properties.find(
		(property) =>
			property.implements.find(
				(impl) => impl.id === builtInConnectionPropertyId,
			) ||
			property.implements.find(
				(impl) => impl.extends?.id === builtInConnectionPropertyId,
			),
	);

	const connectionId = connectionIdProperty?.getDefaultValue()?.value as string;

	// Idempotent script loader
	const loadGooglePickerScript = (): Promise<void> => {
		return new Promise((resolve, reject) => {
			if (window.gapi && window.google?.picker) {
				resolve();
				return;
			}

			const scriptId = 'google-api-script';
			let script = document.getElementById(scriptId) as HTMLScriptElement;

			if (!script) {
				script = document.createElement('script');
				script.id = scriptId;
				script.src = 'https://apis.google.com/js/api.js';
				document.body.appendChild(script);
			}

			script.addEventListener('load', () => {
				window.gapi.load('picker', {
					callback: resolve,
					onerror: reject,
				});
			});

			script.addEventListener('error', reject);
		});
	};

	const handleOpenPicker = async () => {
		if (disabled || !connectionId) return;

		try {
			setIsPicking(true);

			// 1. Fetch credentials from Nango to get the raw access token
			const credentialsData = await project.integrationsService.getCredentials(
				integrationName,
				connectionId,
			);

			// Extract the OAuth access token (Nango usually stores it here for OAuth2)
			const accessToken =
				credentialsData?.credentials?.credentials?.access_token;

			if (!accessToken) {
				throw new Error('No access token found in Nango credentials.');
			}

			// 2. Load the Google Picker API Script dynamically
			await loadGooglePickerScript();

			// 3. Extract GCP configs from env
			const apiKey = project.env.GOOGLE_PICKER_API_KEY;
			const appId = project.env.GOOGLE_PICKER_APP_ID; // Your Project Number

			if (!apiKey || !appId) {
				throw new Error(
					'Google Picker API_KEY or APP_ID environment variables are missing.',
				);
			}

			// 4. Build and render the Picker
			const view = new window.google.picker.DocsView(
        window.google.picker.ViewId.DOCS,
      );
      view.setIncludeFolders(true); // Allows navigating through folders
      
      // Force List view to make multi-select checkboxes more obvious
      // view.setMode(window.google.picker.DocsViewMode.LIST);

      const picker = new window.google.picker.PickerBuilder()
        .addView(view)
        // Override the default misleading title
        .setTitle('Select one or more files to authorize Elyx (you can add more later)')
        .enableFeature(window.google.picker.Feature.MULTISELECT_ENABLED)
        .setOAuthToken(accessToken)
        .setDeveloperKey(apiKey)
        .setAppId(appId)
        .setCallback((data: any) => {
          if (
            data[window.google.picker.Response.ACTION] ===
            window.google.picker.Action.PICKED
          ) {
            const documents = data[window.google.picker.Response.DOCUMENTS];
            Logger.log(
              '[Google Picker] User explicitly authorized files:',
              documents,
            );

            popupNotification({
              text: `Successfully authorized access to ${documents.length} file${documents.length !== 1 ? 's' : ''}.`,
              color: 'success',
              delay: 3000,
            });
          }
        })
        .build();

			picker.setVisible(true);

			// We use a tiny timeout to ensure the iframe has actually mounted in the DOM
			setTimeout(() => {
				window.dispatchEvent(new Event('resize'));
			}, 50);
		} catch (error: any) {
			Logger.error('Failed to open Google Drive Picker:', error);
			popupNotification({
				text: error.message || 'Failed to open the file picker.',
				color: 'error',
				delay: 4000,
			});
		} finally {
			setIsPicking(false);
		}
	};

	return (
		<button
			id={id}
			ref={canvasPopupTooltip.anchorRef}
			onClick={(e) => {
				e.stopPropagation();
				handleOpenPicker();
			}}
			className={['canvas-draggable-card__footer__item'].join(' ')}
		>
			{isPicking ? (
				<i
					className='fa-solid fa-circle-notch fa-spin'
					style={{ opacity: 0.6 }}
				/>
			) : (
				<img
					src={FileSearchIcon}
					style={{
						width: 16,
						height: 16,
						opacity: disabled ? 0.2 : 0.8,
					}}
					alt='File search icon'
				/>
			)}
		</button>
	);
}
